import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { RequestByIdApiType } from '@/api/models/requests'
import { ServerValidationError } from '@/api/shared/types'
import { PassFormHeader } from '@/components/entities/passes/ui/PassForms/FormBlock/PassFormHeader'
import { PassPersonsFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassPersonsFormBlock'
import { RequestDatePickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestDatePickerFormBlock'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import { useFormDirty } from '@/components/entities/requests/ui/hooks/useFormDirty'
import {
  formatPersonsData,
  getDefaultPersons,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'

import {
  generatePersonsSchema,
  PersonsApiData,
  PersonsFormData,
  PersonsFormErrors
} from '../../../requests/ui/model/schemas'

type Props = {
  onFormDirty: (_: boolean) => void
  pass?: RequestByIdApiType
  onSubmit: (data: PersonsApiData) => Promise<void>
  passError?: AxiosError<ServerValidationError> | null
  isPending?: boolean
}

const GuestPassForm: FC<Props> = ({
  onFormDirty,
  pass,
  onSubmit,
  passError,
  isPending
}) => {
  const { rooms } = useSelectedProjectPremises()

  const form = useForm<PersonsFormData, PersonsFormErrors>({
    defaultValues: {
      room: undefined,
      persons: [{ value: '' }]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generatePersonsSchema())
  })

  const { formState, handleSubmit, reset } = form

  useFormDirty(onFormDirty, formState)

  useEffect(() => {
    reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      persons: getDefaultPersons(pass)
    })
  }, [pass, reset, rooms])

  const handleFormSubmit = async (data: PersonsFormData) => {
    await onSubmit(formatPersonsData(data))
  }

  return (
    <DrawerFormLayout
      onSubmit={handleSubmit(handleFormSubmit)}
      isPending={isPending}
      header={
        <PassFormHeader text="Разовый гостевой пропуск. Для проезда на паркинг оформите пропуск для транспортного средства" />
      }
    >
      <div className="form-block-cover mb-16 grid grid-cols-2 gap-24 p-32 lg:grid-cols-1 lg:gap-16 lg:p-16">
        <FormProvider {...form}>
          <RequestDatePickerFormBlock passError={passError} />
        </FormProvider>

        <FormProvider {...form}>
          <RequestRoomPickerFormBlock />
        </FormProvider>
      </div>
      <FormProvider {...form}>
        <PassPersonsFormBlock
          passError={passError}
          modalHeader="Удалить гостя?"
        />
      </FormProvider>
    </DrawerFormLayout>
  )
}

export { GuestPassForm }
